/**
 * Exports for the accountant.
 *
 * These build the data rows only; the calling page prepends a translated header and runs them
 * through `toCsv`. That keeps the column order testable and the wording translatable.
 *
 * Amounts are written with a comma as the decimal separator and no thousands separator, which is
 * what Austrian bookkeeping software (BMD, RZL, DATEV's Austrian import) expects; dates as
 * TT.MM.JJJJ like the BMD file next to them, and codes in words through `ExportLabels` (QA-0057).
 */
import type { TFunction } from "i18next";
import type { Category } from "@/lib/api/categories";
import type { DocumentRecord } from "@/lib/api/documents";
import type { BankAccount, ReceiptGap, Transaction } from "@/lib/api/banking";
import type { UvaResult } from "@/lib/at/uva";
import { UVA_CODES } from "@/lib/at/uva";
import { parseVatLines } from "@/lib/at/vat";
import { toCsv } from "@/lib/utils/csv";

/** 1234.5 -> "1234,50". Empty for a missing amount so the cell stays blank. */
export function money(value: number | undefined | null): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  return value.toFixed(2).replace(".", ",");
}

/** "2026-03-14 00:00:00.000Z" -> "14.03.2026". */
const day = (value: string | undefined) =>
  value && /^\d{4}-\d{2}-\d{2}/.test(value)
    ? `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}`
    : "";

/** The words for the codes a document carries, in the reader's language. */
export type ExportLabels = {
  status: (value: DocumentRecord["status"]) => string;
  paymentStatus: (value: NonNullable<DocumentRecord["paymentStatus"]>) => string;
  taxTreatment: (value: NonNullable<DocumentRecord["taxTreatment"]>) => string;
};

/** Codes as they are stored; what the tests read. */
const CODES: ExportLabels = { status: String, paymentStatus: String, taxTreatment: String };

export function exportLabels(t: TFunction): ExportLabels {
  return {
    status: (value) => t(`status.${value}`),
    paymentStatus: (value) => t(`paymentStatus.${value}`),
    taxTreatment: (value) => t(`taxTreatment.${value}`),
  };
}

export const DOCUMENT_COLUMNS = [
  "docNumber",
  "kind",
  "status",
  "issueDate",
  "serviceDate",
  "partnerName",
  "partnerVatId",
  "invoiceNumber",
  "account",
  "category",
  "taxTreatment",
  "netTotal",
  "vatTotal",
  "grossTotal",
  "tip",
  "vatRates",
  "currency",
  "paymentStatus",
  "paidDate",
  "deductiblePercent",
  "notes",
] as const;

/** One row per document - the journal an accountant books from. */
export function documentRows(
  documents: DocumentRecord[],
  categories: Category[] = [],
  labels: ExportLabels = CODES,
): Array<Array<string | number>> {
  const byId = new Map(categories.map((category) => [category.id, category]));
  return documents.map((document) => {
    const category = document.category ? byId.get(document.category) : undefined;
    const rates = parseVatLines(document.vatLines)
      .map(
        (line) => `${String(line.rate).replace(".", ",")}%: ${money(line.net)}/${money(line.vat)}`,
      )
      .join(" | ");
    return [
      document.docNumber ?? "",
      document.kind,
      labels.status(document.status),
      day(document.issueDate),
      day(document.serviceDate),
      document.partnerName ?? "",
      document.partnerVatId ?? "",
      document.invoiceNumber ?? "",
      category?.accountNumber ?? "",
      category?.name ?? "",
      document.taxTreatment ? labels.taxTreatment(document.taxTreatment) : "",
      money(document.netTotal),
      money(document.vatTotal),
      money(document.grossTotal),
      document.tip ? money(document.tip) : "",
      rates,
      document.currency || "EUR",
      document.paymentStatus ? labels.paymentStatus(document.paymentStatus) : "",
      day(document.paidDate),
      document.deductiblePercent ?? 100,
      document.notes ?? "",
    ];
  });
}

export const TRANSACTION_COLUMNS = [
  "account",
  "bookingDate",
  "valueDate",
  "amount",
  "currency",
  "counterparty",
  "description",
  "reference",
  "status",
  "docNumber",
] as const;

export function transactionRows(
  transactions: Transaction[],
  accounts: BankAccount[] = [],
): Array<Array<string | number>> {
  const byId = new Map(accounts.map((account) => [account.id, account]));
  return transactions.map((transaction) => [
    byId.get(transaction.account)?.label ?? "",
    day(transaction.bookingDate),
    day(transaction.valueDate),
    money(transaction.amount),
    transaction.currency || "EUR",
    transaction.counterparty ?? "",
    transaction.description ?? "",
    transaction.reference ?? "",
    transaction.status,
    transaction.expand?.document?.docNumber ?? "",
  ]);
}

export const MISSING_COLUMNS = [
  "account",
  "bookingDate",
  "amount",
  "counterparty",
  "description",
] as const;

/** Card charges that still have no receipt, grouped by account - the to-do list. */
export function missingReceiptRows(
  gaps: Array<Pick<ReceiptGap, "account" | "missing">>,
): Array<Array<string | number>> {
  return gaps.flatMap((gap) =>
    gap.missing.map((transaction) => [
      gap.account.label,
      day(transaction.bookingDate),
      money(transaction.amount),
      transaction.counterparty ?? "",
      transaction.description ?? "",
    ]),
  );
}

/** The UVA Kennzahlen as "code; amount", ready to type into FinanzOnline. */
export function uvaRows(result: UvaResult): Array<Array<string | number>> {
  return UVA_CODES.filter((code) => result.amounts[code] !== 0 || code === "095").map((code) => [
    code,
    money(result.amounts[code]),
  ]);
}

/** Wraps rows in a header and hands the browser a file. */
export function buildCsv(header: string[], rows: Array<Array<string | number>>): string {
  return toCsv([header, ...rows]);
}

export function downloadCsv(filename: string, csv: string): void {
  downloadFile(filename, new Blob([csv], { type: "text/csv;charset=utf-8" }));
}

export function downloadFile(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  // Revoking right away cancels a large download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
